import { useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  REPLACEMENT_KINDS,
  REPLACEMENT_KIND_LABELS,
  roleCanManageReplacements,
  type ReplacementKind,
  type ReplacementResponse,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

export function ReplacementNewPage(): JSX.Element {
  const { user } = useAuth();
  const canManage = user ? roleCanManageReplacements(user.role) : false;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const paramKind = params.get('kind');
  const [kind, setKind] = useState<ReplacementKind>(
    paramKind === 'broken' || paramKind === 'missing' ? paramKind : 'missing',
  );
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const body = await apiRequest<ReplacementResponse>('/api/replacements', {
        method: 'POST',
        body: {
          kind,
          description,
          assetId: params.get('assetId') || null,
          toteId: params.get('toteId') || null,
          loadoutId: params.get('loadoutId') || null,
          eventId: params.get('eventId') || null,
          inventoryLineId: params.get('inventoryLineId') || null,
        },
      });
      await navigate(`/replacements/${body.replacement.id}`);
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the request.');
    }
  }

  if (!canManage) {
    return (
      <section data-testid="replacement-new">
        <div className="card">
          <p className="status status--error" data-testid="replacement-denied">
            Only an admin or equipment admin can create a replacement request.
          </p>
          <Link to="/">Back home</Link>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="replacement-new">
      <form className="card" onSubmit={(event) => void onSubmit(event)}>
        <h2>New replacement request</h2>
        <p className="meta">Internal ticket. Creating this emails admins and equipment admins.</p>
        {error ? (
          <p className="status status--error" data-testid="replacement-new-error">
            {error}
          </p>
        ) : null}
        <label className="field">
          Kind
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value as ReplacementKind)}
            data-testid="replacement-kind"
          >
            {REPLACEMENT_KINDS.map((option) => (
              <option key={option} value={option}>
                {REPLACEMENT_KIND_LABELS[option]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Description
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            required
            data-testid="replacement-description"
          />
        </label>
        <button type="submit" className="button" data-testid="replacement-create">
          Create request
        </button>
      </form>
    </section>
  );
}
